import type { ReactNode } from 'react';

/**
 * Shared chibi rig for every CrowdSet avatar. Each character supplies colours and a handful of
 * layered shapes; the rig provides the animatable groups:
 *   .cs-av-fig   whole figure (bounce / jump)
 *   .cs-av-head  head (nod / shake / tilt)
 *   .cs-av-arm-l / .cs-av-arm-r   arms (swing / raise)
 * All motion is transform/opacity only (see styles.css).
 * viewBox is 0 0 80 120; feet rest at y≈112.
 */
export interface CharacterSpec {
  skin: string;
  top: string;
  sleeve?: string;
  legs: string;
  shoes: string;
  /** Behind the head and body (long hair, hoods, braids). */
  back?: ReactNode;
  /** Torso details drawn over the base top (stripes, zips, patches). */
  torso?: ReactNode;
  /** Hair/hat drawn over the head. */
  hair?: ReactNode;
  /** Face accessories (glasses, visor, beard) drawn over the face. */
  face?: ReactNode;
  /** Replace the default face (e.g. robot). */
  customHead?: ReactNode;
  /** Extra items over everything on the head (headphones, earrings). */
  headTop?: ReactNode;
  /** Replace default legs (e.g. long coat). */
  lowerOverride?: ReactNode;
  hands?: string;
}

export function Rig({ c }: { c: CharacterSpec }) {
  const hands = c.hands ?? c.skin;
  const sleeve = c.sleeve ?? c.top;
  return (
    <g className="cs-av-fig">
      <ellipse
        className="cs-av-shadow"
        cx="40"
        cy="113"
        rx="17"
        ry="3.2"
        fill="#000"
        opacity=".28"
      />
      {c.back}
      {c.lowerOverride ?? (
        <g>
          <rect x="30.5" y="84" width="8" height="24" rx="3.5" fill={c.legs} />
          <rect x="41.5" y="84" width="8" height="24" rx="3.5" fill={c.legs} />
          <ellipse cx="33.8" cy="109.5" rx="6" ry="3.4" fill={c.shoes} />
          <ellipse cx="46.2" cy="109.5" rx="6" ry="3.4" fill={c.shoes} />
        </g>
      )}
      <g className="cs-av-arm-l">
        <rect x="21" y="55" width="8" height="25" rx="4" fill={sleeve} />
        <circle cx="25" cy="81" r="3.6" fill={hands} />
      </g>
      <g className="cs-av-arm-r">
        <rect x="51" y="55" width="8" height="25" rx="4" fill={sleeve} />
        <circle cx="55" cy="81" r="3.6" fill={hands} />
      </g>
      <path d="M27 60 Q27 52 35 52 L45 52 Q53 52 53 60 L54 86 Q40 90 26 86 Z" fill={c.top} />
      {c.torso}
      <rect x="36.5" y="47" width="7" height="7" rx="2" fill={c.skin} />
      <g className="cs-av-head">
        {c.customHead ?? (
          <>
            <circle cx="40" cy="31" r="17" fill={c.skin} />
            <ellipse cx="33.5" cy="33" rx="1.9" ry="2.4" fill="#1d1a22" />
            <ellipse cx="46.5" cy="33" rx="1.9" ry="2.4" fill="#1d1a22" />
            <circle cx="30" cy="38.5" r="2.4" fill="#e7837a" opacity=".35" />
            <circle cx="50" cy="38.5" r="2.4" fill="#e7837a" opacity=".35" />
            <path
              className="cs-av-mouth"
              d="M36.5 39.5 Q40 42.5 43.5 39.5"
              stroke="#1d1a22"
              strokeWidth="1.5"
              fill="none"
              strokeLinecap="round"
            />
          </>
        )}
        {c.face}
        {c.hair}
        {c.headTop}
      </g>
    </g>
  );
}
